// Rodapé em colunas. Para acrescentar uma coluna ou ligação (legal, comunidade…) basta mexer em `footerColumns`:
// cada ligação tem a chave i18n do texto, o destino e, se for caso disso, a condição para aparecer.
import type { SiteSections } from "@ecliptica/config/site-sections";
import { Logo } from "@ecliptica/game-ui";
import { useTranslation, type Lang } from "@ecliptica/i18n";
import { Link } from "react-router";
import { accountPath, helpPath, legalPath, pathFor, sitePath } from "../lib/lang";
import { downloadPanels } from "../lib/sections";

type FooterLink = {
  /** Chave i18n do texto. */
  label: string;
  /** Destino: "hash" = âncora na página inicial; "page" = página do site (rota do router). */
  to: { hash: string } | { page: string };
  /** Falso → a ligação não aparece (ex.: parte do site desligada). */
  show?: boolean;
};
export type FooterColumn = { title: string; links: FooterLink[] };

/** Colunas do rodapé para a língua e as partes do site atuais. */
export function footerColumns(lang: Lang, on: SiteSections): FooterColumn[] {
  const columns: FooterColumn[] = [
    {
      title: "foot.game",
      links: [
        { label: "nav.download", to: { hash: "download" }, show: downloadPanels(on).length > 0 },
        { label: "nav.cards", to: { page: sitePath(lang, "cards") }, show: on.cards },
        { label: "nav.world", to: { page: sitePath(lang, "world") }, show: on.world },
        { label: "nav.books", to: { hash: "books" }, show: on.books },
      ],
    },
    {
      title: "foot.helpCol",
      links: [
        { label: "nav.help", to: { page: helpPath(lang, "help") } },
        { label: "foot.support", to: { page: helpPath(lang, "helpNew") } },
        { label: "nav.account", to: { page: accountPath(lang, "account") } },
      ],
    },
    {
      title: "foot.legalCol",
      links: [
        { label: "foot.terms", to: { page: legalPath(lang, "terms") }, show: on.legal },
        { label: "foot.privacy", to: { page: legalPath(lang, "privacy") }, show: on.legal },
        { label: "foot.cookies", to: { page: legalPath(lang, "cookies") }, show: on.legal },
      ],
    },
  ];
  return columns.map((c) => ({ ...c, links: c.links.filter((l) => l.show !== false) })).filter((c) => c.links.length > 0);
}

const LINK = "opacity-85 hover:text-gold-bright hover:underline hover:opacity-100";

export function Footer({ lang, sections }: { lang: Lang; sections: SiteSections }) {
  const { t } = useTranslation();
  const home = pathFor(lang);
  const columns = footerColumns(lang, sections);
  return (
    <footer className="border-t px-[6vw] pb-12 pt-12 text-sm text-muted-foreground">
      <div className="mx-auto flex max-w-5xl flex-col gap-10 sm:flex-row sm:justify-between">
        <div className="sm:max-w-xs">
          <Logo tagline className="mb-3 text-[13px]" />
          <p>{t("foot.rights")}</p>
        </div>
        <nav aria-label={t("foot.nav")} className="flex flex-wrap gap-x-14 gap-y-8">
          {columns.map((c) => (
            <div key={c.title}>
              <h2 className="mb-2.5 font-title text-xs uppercase tracking-[0.18em] text-gold">{t(c.title)}</h2>
              <ul className="space-y-1.5">
                {c.links.map((l) => (
                  <li key={l.label}>
                    {"hash" in l.to ? (
                      <a href={`${home}#${l.to.hash}`} className={LINK}>
                        {t(l.label)}
                      </a>
                    ) : (
                      <Link to={l.to.page} className={LINK}>
                        {t(l.label)}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </div>
      <p className="mx-auto mt-10 max-w-5xl border-t pt-6 text-xs opacity-80">{t("foot.credits")}</p>
    </footer>
  );
}
